
import * as React from 'react';
import { IComponentDecorator, IDecoratedProps, withDisplayName } from '../../../utilities/components/Decorators';
import { IDetailsListProps, IDetailsRowProps, DetailsRow, IColumn } from 'office-ui-fabric-react/lib/DetailsList';
import { ShimmeredDetailsList } from 'office-ui-fabric-react/lib/ShimmeredDetailsList';
import { Shimmer } from '@uifabric/experiments/lib/Shimmer';
import { IItemCache } from '../../../models/IItemCacheObserver';
import { connectToItemCache } from '../../decorators/connectToItemCache';
import { enableShimmeredDetailsListConfigurationFacet, configurationItemKey } from '../../../facets/ConfigurationFacets';

interface IShimmeredDetailsListProps {
  enableShimmeredDetailsList?: boolean;
}

/**
 * Decorator which applies shimmer around the details list.
 */
export function withDetailsListShimmer(): IComponentDecorator<{}, {}, IDetailsListProps> {
  return <P extends IDetailsListProps>(
    component: React.ComponentType<P>
  ): React.ComponentType<IDecoratedProps<P, {}, {}>> => {
    const Component = component;

    const Shimmerable = (props: P & IShimmeredDetailsListProps): JSX.Element => {
      const {
        enableShimmeredDetailsList = true,
        ...detailsListProps
      } = props as IDetailsListProps & IShimmeredDetailsListProps;

      if (enableShimmeredDetailsList) {
        const {
          enableShimmer
        } = detailsListProps;

        return (
          <ShimmeredDetailsList
            { ...detailsListProps }
            enableShimmer={ !!enableShimmer && detailsListProps.items.length === 0 }
          />
        );
      }

      return (
        <Component
          { ...props }
          onRenderMissingItem={ renderMissingItem }
          columns={ createShimmerColumns(props.columns, props.enableShimmer) }
        />
      );
    };

    const Connected = withDisplayName('WithDetailsListShimmer')(connectToItemCache((itemCache: IItemCache): IShimmeredDetailsListProps => {
      return {
        enableShimmeredDetailsList: itemCache.demandItemFacet(enableShimmeredDetailsListConfigurationFacet, configurationItemKey)
      };
    })(Shimmerable));

    return Connected as {} as React.ComponentType<IDecoratedProps<P, {}, {}>>;
  };
}

/**
 * Render function to run on missing items of the final array passed to DetailsList.
 */
function renderMissingItem(
  index: number | undefined,
  rowProps: IDetailsRowProps | undefined
): React.ReactNode {
  if (!rowProps) {
    return null;
  }

  const {
    ...detailsRowProps
  }: IDetailsRowProps = rowProps;

  return (
    <Shimmer
      isBaseStyle={ true }
    >
      <DetailsRow
        { ...detailsRowProps }
        shimmer={ true }
      />
    </Shimmer>
  );
}

function createShimmerColumns(columns?: IColumn[], enableShimmer?: boolean): IColumn[] | undefined {
  return enableShimmer && columns ? columns.map((column: IColumn): IColumn => {
    return {
      ...column,
      key: `${column.key}-shimmer`
    };
  }) : columns;
}
